import * as React from "react";
import { Minus, Plus } from "lucide-react";

import { Button } from "@/components/shadcn/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/shadcn/dialog.tsx";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "@/components/ExternalLink.tsx";
import { constants } from "@/constants";
import { alertError } from "@/utils/utils.ts";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { LoadingCircle } from "@/components/Loading";
import {Icon} from "@/components/Icon.tsx";

const amountMin = 100;
const amountMax = 10000;
const amountStep = 100;

interface TopUpModalTochkaProps {
  onPromocodeClick: () => void;
}

export const TopUpModalTochka = ({
  onPromocodeClick,
}: TopUpModalTochkaProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [amount, setAmount] = React.useState(290);
  const [btnLoading, setBtnLoading] = React.useState("");

  function onClick(adjustment: number) {
    if (amount > 2000) {
      adjustment = adjustment > 0 ? 200 : -200;
    }

    setAmount(Math.max(amountMin, Math.min(amountMax, amount + adjustment)));
  }

  async function tochkaLinkHandler(paymentMethod: string) {
    try {
      let url = `${constants.url.API_PREFIX}tochka/create-link`;
      const response = await fetch(url, {
        method: "POST", // Метод запроса
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: amount,
          method: paymentMethod,
        }),
      });

      let respData = await response.json();
      if (respData.link) {
        window.location.href = respData.link;
      } else {
        alertError(t("alerts.default_error_message"));
      }
    } catch (e) {
      alertError(t("alerts.default_error_message"));
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="default" className="[&_svg]:size-6">
          <Icon icon="famicons:card-outline" />
          {t("billing.top_up")}
        </Button>
      </DialogTrigger>
      <DialogContent className="px-1 md:px-6 sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-center">Сумма пополнения</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            className="bg-slate-100 h-8 w-8 shrink-0 rounded-full"
            onClick={() => onClick(-amountStep)}
            disabled={amount <= amountMin}
          >
            <Minus />
            <span className="sr-only">Decrease</span>
          </Button>

          <div className="w-40 text-center">
            <div className="text-5xl font-bold tracking-tighter">{amount}</div>
            <div className="text-[0.70rem] uppercase text-muted-foreground">
              RUB
            </div>
          </div>

          <Button
            variant="outline"
            size="icon"
            className="bg-slate-100 h-8 w-8 shrink-0 rounded-full"
            onClick={() => onClick(amountStep)}
            disabled={amount >= amountMax}
          >
            <Plus />
            <span className="sr-only">Increase</span>
          </Button>
        </div>

        <Button
          variant="default"
          className="h-16 text-2xl [&_svg]:size-6"
          onClick={() => {
            tochkaLinkHandler("sbp");
            setBtnLoading("sbp")
          }}
        >
          <img src="/account/logo/SBP.svg" className="h-10" />
          СБП
          {!isMobile && <span>&nbsp;(QR код)</span>}
          {btnLoading == "sbp" && <LoadingCircle />}
        </Button>

        <Button
          variant="outline"
          className="[&_svg]:size-10 h-16 text-lg"
          onClick={() => {
            tochkaLinkHandler("card");
            setBtnLoading("card")
          }}
        >
          <Icon icon="famicons:card-outline" />
          Оплата картой
          {btnLoading == "card" && <span className="[&_svg]:size-6"><LoadingCircle /></span>}
        </Button>

        <Button
          variant="outline"
          className="[&_svg]:size-10 h-16 text-lg"
          onClick={onPromocodeClick}
        >
          <Icon icon="mdi:coupon-outline" />
          Применить промокод
        </Button>

        <DialogFooter className="block text-gray-500 text-sm pt-8">
          <div className="flex items-center gap-4 my-4 border rounded-lg p-2 bg-sky-50">
            <img
              src={`${constants.baseURL}/logo/integrations/telegram.svg`}
              className="w-10 rounded-lg"
            />
            <div>
              Подписывайтесь на
              <ExternalLink href="https://t.me/mostAI_com">
                наш телеграм канал
              </ExternalLink>
              чтобы не пропустить полезные новости из мира ИИ, акции и промокоды
            </div>
          </div>
          Минимальная сумма пополнения 100 руб. Переходя к оплате вы
          соглашаетесь с договором{" "}
          <ExternalLink href="https://most-ai.com/offer">оферты</ExternalLink>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
